import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconMessageCircle } from '@tabler/icons-react';
import { useMingo } from '../context/MingoContext';
import { canEnterMainApp } from '../lib/onboardingStorage';
import { claudeUserMessage, isClaudeConfigured } from '../lib/claude';

const FAQ = [
  { qKey: 'valueAdd.botFaq1q', aKey: 'valueAdd.botFaq1a' },
  { qKey: 'valueAdd.botFaq2q', aKey: 'valueAdd.botFaq2a' },
  { qKey: 'valueAdd.botFaq3q', aKey: 'valueAdd.botFaq3a' },
];

const SYSTEM_HINT =
  'You are Mingo, a short helpful assistant for an expat safety & life-info app in Vietnam (Da Nang focus). ' +
  'Answer in the user language when possible. Be concise (max 6 sentences). No medical/legal certainty; suggest official sources for visa.';

function ChatBot() {
  const { t, language } = useMingo();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  const pushBot = useCallback((text) => {
    setMsgs((m) => [...m, { role: 'bot', text }]);
  }, []);

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', text: q }]);
    setBusy(true);
    try {
      if (isClaudeConfigured()) {
        const reply = await claudeUserMessage(`${SYSTEM_HINT}\nUser (${language || 'ko'}) asks: ${q}`, 900);
        pushBot(reply);
      } else {
        pushBot(t('valueAdd.botNoKey'));
      }
    } catch {
      pushBot(t('valueAdd.botErr'));
    } finally {
      setBusy(false);
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    }
  }, [busy, input, language, pushBot, t]);

  const hidden =
    !canEnterMainApp() ||
    location.pathname.startsWith('/splash') ||
    location.pathname.startsWith('/onboarding/') ||
    location.pathname !== '/settings/account';
  if (hidden) return null;

  return (
    <>
      <button type="button" className="mingo-chat-fab" aria-label={t('valueAdd.botFab')} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <IconMessageCircle size={22} stroke={2} aria-hidden />
      </button>
      {open ? (
        <div className="mingo-chat-root" role="presentation">
          <button type="button" className="mingo-chat-backdrop" aria-label={t('common.close')} onClick={() => setOpen(false)} />
          <div className="mingo-chat-panel" role="dialog" aria-modal="true" aria-label={t('valueAdd.botTitle')}>
            <header className="mingo-chat-panel__head">
              <p className="mingo-chat-panel__title">{t('valueAdd.botTitle')}</p>
              <button type="button" className="mingo-chat-panel__x" onClick={() => setOpen(false)}>
                ×
              </button>
            </header>
            <p className="mingo-chat-panel__lead">{t('valueAdd.botLead')}</p>
            <div className="mingo-chat-chips">
              <button type="button" className="mingo-chat-chip" onClick={() => setInput(t('valueAdd.botChipVisa'))}>
                {t('valueAdd.botChipVisa')}
              </button>
              <button type="button" className="mingo-chat-chip" onClick={() => setInput(t('valueAdd.botChipServices'))}>
                {t('valueAdd.botChipServices')}
              </button>
              <button type="button" className="mingo-chat-chip" onClick={() => setInput(t('valueAdd.botChipSafety'))}>
                {t('valueAdd.botChipSafety')}
              </button>
            </div>
            <div className="mingo-chat-faq">
              {FAQ.map((f) => (
                <button key={f.qKey} type="button" className="mingo-chat-faq__btn" onClick={() => pushBot(`${t(f.qKey)}\n\n${t(f.aKey)}`)}>
                  {t(f.qKey)}
                </button>
              ))}
            </div>
            <div ref={listRef} className="mingo-chat-msgs" role="log">
              {msgs.map((m, i) => (
                <div key={i} className={`mingo-chat-msg mingo-chat-msg--${m.role}`}>
                  {m.text}
                </div>
              ))}
              {busy ? <div className="mingo-chat-msg mingo-chat-msg--bot">{t('valueAdd.botTyping')}</div> : null}
            </div>
            <div className="mingo-chat-inputrow">
              <input
                className="mingo-write__input mingo-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('valueAdd.botPh')}
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="send"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void send();
                }}
              />
              <button type="button" className="mingo-chat-send" onClick={() => void send()} disabled={busy}>
                {t('valueAdd.botSend')}
              </button>
            </div>
            <p className="mingo-chat-disclaimer">{t('valueAdd.botDisclaimer')}</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default ChatBot;
